/**
 * CanvasViewGridRenderer - Runtime view-grid element for authenticated canvases.
 *
 * Binds a canvas element to a MyVault view data source and reuses the same
 * ViewGridNew/view-rows path as the full view page. Public canvas rendering is
 * deliberately not wired here because view rows are workspace-authenticated data.
 */

import React, { useCallback, useContext, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { useLocation, useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import { ViewGridNew, ViewGridSkeleton } from "../../viewGrid";
import { readColumnDisplay } from "../../viewGrid/columnDisplay";
import CanvasRowList, { readRowListConfig } from "./CanvasRowList";
import type { ColumnFilter } from "../../viewGrid/serverFilters";
import {
  useCreateRowMutation,
  useDeleteRowMutation,
  useUpdateRowMutation,
  useViewDataQuery,
  useViewQuery,
} from "../../../hooks/useViewDataQuery";
import type {
  CanvasDataSource,
  CanvasNavigationAction,
  CanvasViewGridFilterControl,
  FilterOperator,
  ViewField,
} from "../../../types";
import type { RenderableElement } from "../CanvasRenderer";
import { useCanvasRuntime } from "../CanvasRuntimeContext";
import { CanvasAutoHeightContext } from "../contentHeight";
import { CanvasViewGridPlaceholder } from "./CanvasViewGridPlaceholder";
import { useAuthFetch } from "../../../../../utils/useAuthFetch";
import { openCanvasNavigation } from "../canvasNavigation";
import type { CanvasWriteContext } from "../../../services/viewService";

type ViewGridSort = { field: string; direction: "ASC" | "DESC" };

type CanvasViewGridConfig = {
  data_source_alias?: string;
  title?: string;
  visible_columns?: string[];
  page_size?: number;
  enable_search?: boolean;
  enable_sort?: boolean;
  enable_pagination?: boolean;
  allow_create?: boolean;
  allow_edit?: boolean;
  allow_delete?: boolean;
  row_navigation?: boolean;
  row_click_action?: CanvasNavigationAction | string;
  default_sort?: ViewGridSort[];
  default_filters?: Array<{
    field: string;
    operator: FilterOperator;
    value?: unknown;
  }>;
  filter_controls?: CanvasViewGridFilterControl[];
  height?: number;
  display?: string;
  list_config?: Record<string, unknown>;
};

interface CanvasViewGridRendererProps {
  element: RenderableElement;
  dataSources: CanvasDataSource[];
  canvasId?: string | null;
}

function getConfig(element: RenderableElement): CanvasViewGridConfig {
  return element.props as CanvasViewGridConfig;
}

function resolveDataSource(
  dataSources: CanvasDataSource[],
  alias?: string,
): CanvasDataSource | undefined {
  const requestedAlias = alias || "main";
  return dataSources.find((source) => {
    const sourceAlias = source.name || source.id;
    return sourceAlias === requestedAlias || source.id === requestedAlias;
  });
}

export function isSearchableField(field: ViewField): boolean {
  if (field.is_visible === false || field.visible === false) return false;
  const dataType = field.column?.data_type?.toLowerCase() || "";
  return (
    !dataType ||
    dataType.includes("char") ||
    dataType.includes("text") ||
    dataType.includes("json") ||
    dataType.includes("uuid") ||
    dataType.includes("uniqueidentifier")
  );
}

function getErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : undefined;
}

function getErrorMessage(error: unknown): string | null {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  const detail = (error as { response?: { detail?: unknown } } | null)?.response
    ?.detail;
  return typeof detail === "string" ? detail : null;
}

export function CanvasViewGridRenderer({
  element,
  dataSources,
  canvasId,
}: CanvasViewGridRendererProps) {
  const runtime = useCanvasRuntime();
  const autoHeight = useContext(CanvasAutoHeightContext);
  const navigate = useNavigate();
  const location = useLocation();
  const authFetch = useAuthFetch();
  const [navigationError, setNavigationError] = useState<string | null>(null);
  const config = getConfig(element);
  const columnDisplay = useMemo(
    () => readColumnDisplay(config as unknown as Record<string, unknown>),
    [config],
  );
  const rowList = useMemo(
    () => readRowListConfig(config as unknown as Record<string, unknown>),
    [config],
  );
  const dataSourceAlias = config.data_source_alias || "main";
  const dataSource = resolveDataSource(dataSources, dataSourceAlias);
  const viewId = dataSource?.view_id ? String(dataSource.view_id) : null;
  const effectiveCanvasId = canvasId ?? runtime?.canvasId ?? null;
  const writeContext = useMemo<CanvasWriteContext | undefined>(
    () =>
      effectiveCanvasId
        ? {
            canvasId: effectiveCanvasId,
            elementId: element.id,
            embeddingPath: runtime?.embeddingPath,
          }
        : undefined,
    [effectiveCanvasId, element.id, runtime?.embeddingPath],
  );
  const selectionDataSourceAliases = useMemo(
    () =>
      Array.from(
        new Set(
          [
            dataSourceAlias,
            dataSource?.id,
            dataSource?.name,
            (dataSource as unknown as { alias?: string })?.alias,
          ]
            .filter(Boolean)
            .map(String),
        ),
      ),
    [dataSource, dataSourceAlias],
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(config.page_size || 25);
  const [sort, setSort] = useState<ViewGridSort[]>(config.default_sort || []);
  const [searchTerm, setSearchTerm] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);
  const [filterValues, setFilterValues] = useState<
    Record<string, string | null>
  >(() =>
    Object.fromEntries(
      (config.filter_controls || []).map((control) => [
        control.field,
        control.default_value ?? null,
      ]),
    ),
  );

  const writable = element.effectiveAccess !== "read";
  const allowCreate = writable && config.allow_create === true;
  const allowEdit = writable && config.allow_edit === true;
  const allowDelete = writable && config.allow_delete === true;
  const enableSearch = config.enable_search !== false;
  const enableSort = config.enable_sort !== false;

  const {
    data: view,
    isLoading: viewLoading,
    error: viewError,
  } = useViewQuery(viewId);

  const searchFilters = useMemo(() => {
    const term = searchTerm.trim();
    if (!term || !view) return undefined;

    const fields = view.fields
      .filter((field) => isSearchableField(field))
      .map((field) => String(field.id));

    if (fields.length === 0) return undefined;

    return fields.map((field) => ({
      field,
      operator: "contains" as const,
      value: term,
    }));
  }, [searchTerm, view]);

  const runtimeFilters = useMemo(() => {
    return (
      runtime?.interactionBus.getRuntimeFilters({
        canvasId: effectiveCanvasId,
        elementId: element.id,
        dataSourceAlias,
      }) || []
    );
  }, [dataSourceAlias, effectiveCanvasId, element.id, runtime?.interactionBus]);

  const relationshipFilters = useMemo(() => {
    return runtimeFilters.flatMap((runtimeFilter) => runtimeFilter.filters);
  }, [runtimeFilters]);

  const userFilters = useMemo(
    () =>
      (config.filter_controls || []).flatMap((control) => {
        const value = filterValues[control.field];
        return value === null || value === undefined || value === ""
          ? []
          : [{ field: control.field, operator: control.operator, value }];
      }),
    [config.filter_controls, filterValues],
  );

  const filters = useMemo(() => {
    return [
      ...relationshipFilters,
      ...(config.default_filters || []),
      ...userFilters,
      ...columnFilters,
    ];
  }, [columnFilters, config.default_filters, relationshipFilters, userFilters]);

  const filterGroups = useMemo(
    () =>
      searchFilters?.length
        ? [{ logic: "OR" as const, filters: searchFilters }]
        : undefined,
    [searchFilters],
  );

  const {
    data,
    isLoading: dataLoading,
    error: dataError,
    refetch,
  } = useViewDataQuery(viewId, {
    canvasId: effectiveCanvasId || undefined,
    page: page + 1,
    pageSize,
    filters,
    filterLogic: "AND",
    filterGroups,
    sort: enableSort && sort.length > 0 ? sort : undefined,
  });

  const createRowMutation = useCreateRowMutation();
  const updateRowMutation = useUpdateRowMutation();
  const deleteRowMutation = useDeleteRowMutation();

  const handleSort = useCallback(
    (field: string, direction: "ASC" | "DESC") => {
      if (!enableSort) return;
      setSort([{ field, direction }]);
      setPage(0);
    },
    [enableSort],
  );

  const handleColumnFilterChange = useCallback((next: ColumnFilter[]) => {
    setColumnFilters(next);
    setPage(0);
  }, []);

  const handlePageSizeChange = useCallback((nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(0);
  }, []);

  const handleFilterChange = useCallback(
    (field: string, value: string | null) => {
      const nextValues = { ...filterValues, [field]: value };
      setFilterValues(nextValues);
      setPage(0);
      const selection = runtime?.interactionBus.getSelectedRow({
        canvasId: effectiveCanvasId,
        elementId: element.id,
        dataSourceAlias,
      });
      if (!selection) return;
      const excluded = (config.filter_controls || []).some((control) => {
        const selected = nextValues[control.field];
        if (!selected) return false;
        const rowValue = selection.rowData[control.field];
        return control.operator === "ne"
          ? String(rowValue) === selected
          : String(rowValue) !== selected;
      });
      if (excluded) {
        runtime?.interactionBus.dispatchInteraction({
          type: "selection_cleared",
          eventId: `filter-cleared:${element.id}:${Date.now()}`,
          elementId: element.id,
          canvasId: effectiveCanvasId,
          dataSourceAlias,
          dataSourceAliases: selectionDataSourceAliases,
        });
      }
    },
    [
      config.filter_controls,
      dataSourceAlias,
      effectiveCanvasId,
      element.id,
      filterValues,
      runtime?.interactionBus,
      selectionDataSourceAliases,
    ],
  );

  const handleRowCreate = useCallback(
    async (rowData: Record<string, unknown>) => {
      if (!viewId || !allowCreate) return;
      await createRowMutation.mutateAsync({
        viewId,
        data: rowData,
        context: writeContext,
      });
    },
    [allowCreate, createRowMutation, viewId, writeContext],
  );

  const handleRowUpdate = useCallback(
    async (rowId: string, rowData: Record<string, unknown>) => {
      if (!viewId || !allowEdit) return;
      await updateRowMutation.mutateAsync({
        viewId,
        rowId,
        data: rowData,
        context: writeContext,
      });
    },
    [allowEdit, updateRowMutation, viewId, writeContext],
  );

  const handleRowDelete = useCallback(
    async (rowId: string) => {
      if (!viewId || !allowDelete) return;
      await deleteRowMutation.mutateAsync({
        viewId,
        rowId,
        context: writeContext,
      });
      const selection = runtime?.interactionBus.getSelectedRow({
        canvasId: effectiveCanvasId,
        elementId: element.id,
        dataSourceAlias,
      });
      if (selection?.rowId === rowId) {
        runtime?.interactionBus.dispatchInteraction({
          type: "selection_cleared",
          elementId: element.id,
          canvasId: effectiveCanvasId,
          dataSourceAlias,
          dataSourceAliases: selectionDataSourceAliases,
        });
      }
    },
    [
      allowDelete,
      dataSourceAlias,
      deleteRowMutation,
      effectiveCanvasId,
      element.id,
      writeContext,
      runtime?.interactionBus,
      selectionDataSourceAliases,
      viewId,
    ],
  );

  const handleSearchChange = useCallback(
    (term: string) => {
      setSearchTerm(term);
      setPage(0);
      const selection = runtime?.interactionBus.getSelectedRow({
        canvasId: effectiveCanvasId,
        elementId: element.id,
        dataSourceAlias,
      });
      if (!selection || !term.trim() || !view) return;
      const searchable = view.fields.filter((field) =>
        isSearchableField(field),
      );
      if (!searchable.length) return;
      const match = searchable.some((field) => {
        const key =
          field.alias || field.column?.column_name || String(field.id);
        return String(selection.rowData[key] ?? "")
          .toLowerCase()
          .includes(term.trim().toLowerCase());
      });
      if (!match)
        runtime?.interactionBus.dispatchInteraction({
          type: "selection_cleared",
          elementId: element.id,
          canvasId: effectiveCanvasId,
          dataSourceAlias,
          dataSourceAliases: selectionDataSourceAliases,
        });
    },
    [
      dataSourceAlias,
      effectiveCanvasId,
      element.id,
      runtime?.interactionBus,
      selectionDataSourceAliases,
      view,
    ],
  );

  const handleRowSelect = useCallback(
    (rowId: string, rowData: Record<string, unknown>) => {
      const navigation = config.row_click_action;
      if (
        config.row_navigation &&
        typeof navigation === "object" &&
        navigation.type === "NAVIGATE"
      ) {
        if (!runtime) {
          setNavigationError("Destination unavailable or access denied.");
          return;
        }
        setNavigationError(null);
        void openCanvasNavigation(navigation.config, {
          workspaceId: runtime.workspaceId,
          currentRowId: runtime.rowId,
          selectedRowId: rowId,
          returnPath: location.pathname + location.search,
          authFetch,
          navigate,
        }).catch((error: unknown) => {
          setNavigationError(
            error instanceof Error
              ? error.message
              : "Destination unavailable or access denied.",
          );
        });
        return;
      }
      if (!runtime?.interactionBus || !viewId) return;
      const currentSelection = runtime.interactionBus.getSelectedRow({
        canvasId: effectiveCanvasId,
        elementId: element.id,
        dataSourceAlias,
      });
      if (currentSelection?.rowId === rowId) {
        runtime.interactionBus.dispatchInteraction({
          type: "selection_cleared",
          eventId: `row-cleared:${element.id}:${rowId}:${Date.now()}`,
          elementId: element.id,
          canvasId: effectiveCanvasId,
          dataSourceAlias,
          dataSourceAliases: selectionDataSourceAliases,
        });
        return;
      }

      const provenancePath = Array.from(
        new Set(
          runtimeFilters.flatMap((runtimeFilter) => [
            runtimeFilter.dependsOnSelectionKey,
            ...runtimeFilter.provenancePath,
          ]),
        ),
      );
      runtime.interactionBus.dispatchInteraction({
        type: "row_selected",
        eventId: `row-selected:${element.id}:${rowId}:${Date.now()}`,
        elementId: element.id,
        canvasId: effectiveCanvasId,
        dataSourceAlias,
        dataSourceAliases: selectionDataSourceAliases,
        viewId,
        rowId,
        rowData,
        provenancePath,
      });
    },
    [
      dataSourceAlias,
      config.row_click_action,
      config.row_navigation,
      effectiveCanvasId,
      element.id,
      authFetch,
      location.pathname,
      location.search,
      navigate,
      runtime,
      runtimeFilters,
      selectionDataSourceAliases,
      viewId,
    ],
  );

  if (!dataSource) {
    return (
      <CanvasViewGridPlaceholder
        title={config.title}
        message={`Data source "${config.data_source_alias || "main"}" is not configured.`}
        color="warning"
      />
    );
  }

  if (!viewId) {
    return (
      <CanvasViewGridPlaceholder
        title={config.title}
        message={`Data source "${dataSource.name || dataSource.id}" is not bound to a view.`}
        color="warning"
      />
    );
  }

  const error = viewError || dataError;
  const errorStatus = getErrorStatus(error);
  const errorMessage = getErrorMessage(error);

  if (viewLoading && !view) {
    return <ViewGridSkeleton rows={4} columns={4} />;
  }

  if (!view) {
    return (
      <CanvasViewGridPlaceholder
        title={config.title}
        message={errorMessage || "View not found."}
        color="danger"
      />
    );
  }

  if (errorStatus === 401) {
    return (
      <CanvasViewGridPlaceholder
        title={config.title}
        message="Sign in to load these records."
        color="warning"
      />
    );
  }

  if (errorStatus === 403) {
    return (
      <CanvasViewGridPlaceholder
        title={config.title}
        message="You do not have access to these records."
        color="warning"
      />
    );
  }

  if (errorMessage) {
    return (
      <CanvasViewGridPlaceholder
        title={config.title}
        message={errorMessage}
        color="danger"
      />
    );
  }

  return (
    <Box
      sx={{
        height: autoHeight ? "auto" : "100%",
        minHeight: 260,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {navigationError && (
        <Typography color="danger" level="body-sm">
          {navigationError}
        </Typography>
      )}
      {(config.title || enableSearch) && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ mb: 1, alignItems: { sm: "center" } }}
        >
          {config.title && (
            <Typography level="title-md" sx={{ flex: 1 }}>
              {config.title}
            </Typography>
          )}
          {enableSearch && (
            <Input
              size="sm"
              startDecorator={<SearchIcon fontSize="small" />}
              placeholder="Search all records"
              value={searchTerm}
              onChange={(event) => handleSearchChange(event.target.value)}
              sx={{ minWidth: 220 }}
            />
          )}
        </Stack>
      )}

      {(config.filter_controls || []).length > 0 && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ mb: 1, flexWrap: "wrap" }}
        >
          {config.filter_controls?.map((control, index) => {
            return (
              <FormControl
                key={`${control.field}-${index}`}
                size="sm"
                sx={{ minWidth: 150 }}
              >
                <FormLabel>{control.label}</FormLabel>
                <Select
                  aria-label={control.label}
                  value={filterValues[control.field] ?? null}
                  placeholder="All"
                  onChange={(_, value) =>
                    handleFilterChange(control.field, value)
                  }
                >
                  <Option value="">All</Option>
                  {control.options.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </FormControl>
            );
          })}
        </Stack>
      )}

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {!dataLoading && data?.rows.length === 0 ? (
          <CanvasViewGridPlaceholder
            title={config.title}
            message="No records yet."
            color="neutral"
          />
        ) : rowList && data ? (
          <CanvasRowList
            rows={data.rows}
            config={rowList}
            title={config.title}
            onRowSelect={handleRowSelect}
            page={page}
            pageSize={pageSize}
            totalCount={data.totalCount}
            onPageChange={
              config.enable_pagination === false ? undefined : setPage
            }
          />
        ) : (
          <ViewGridNew
            view={view}
            data={data || null}
            loading={
              dataLoading ||
              createRowMutation.isPending ||
              updateRowMutation.isPending ||
              deleteRowMutation.isPending
            }
            error={null}
            page={config.enable_pagination === false ? 0 : page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            onSort={enableSort ? handleSort : undefined}
            onRowCreate={allowCreate ? handleRowCreate : undefined}
            onRowUpdate={allowEdit ? handleRowUpdate : undefined}
            onRowDelete={allowDelete ? handleRowDelete : undefined}
            onRowSelect={handleRowSelect}
            onRefresh={() => refetch()}
            showToolbar={allowCreate || allowDelete}
            showGridToolbar={false}
            height={
              config.height ||
              (autoHeight
                ? Math.max((data?.rows.length || 1) * 52 + 108, 260)
                : Math.max(element.rowSpan * 60 - 56, 260))
            }
            visibleColumns={config.visible_columns}
            columnDisplay={columnDisplay}
            onColumnFilterChange={handleColumnFilterChange}
            writeContext={writeContext}
            allowCreate={allowCreate}
            allowEdit={allowEdit}
            allowDelete={allowDelete}
            allowBulkActions={false}
            allowImport={false}
            allowAuditLog={false}
          />
        )}
      </Box>
    </Box>
  );
}

export default CanvasViewGridRenderer;
